import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, AlertCircle, FileText, Image, ChevronLeft, ChevronRight,
  Maximize2, Minimize2, ZoomIn, ZoomOut, Loader2
} from 'lucide-react';
import { createApryseWebViewer } from '../../../utils/apryseViewer';
import ContractAnnotationToolbar from '../contract/ContractAnnotationToolbar';
import {
  CONTRACT_TOOLS,
  DEFAULT_FADE_SECONDS,
  getToolById,
  getColorById,
} from '../contract/contractAnnotationTools';
import { useMeetingState } from '../../../context/MeetingStateContext';
import useIsMobile from '../../../hooks/useIsMobile';

/**
 * PresentationStageRenderer - Apryse WebViewer-Based Viewer
 *
 * Renders PDF/Office presentations using Apryse WebViewer.
 * Supports host navigation controls and participant sync.
 */
export default function PresentationStageRenderer({
  presentation,
  presentationState,
  onStateUpdate,
  onPresentationEnd,
  isHost = true,
  onPageChange,
  leftSidebarCollapsed = true,
  rightSidebarCollapsed = true,
  isRecorderMode = false,
}) {
  const fileUrl = presentation?.fileUrl || presentation?.data?.fileUrl;
  const fileType = presentation?.fileType || presentation?.data?.fileType || 'pdf';
  const name = presentation?.name || 'Presentation';

  const meetingState = useMeetingState();
  const presentationId =
    presentation?._id || presentation?.id || presentation?.elementId || 'presentation';

  const isMobile = useIsMobile();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [scale, setScale] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(false);

  // Host annotation state — mirrors ApryseContractRenderer
  const [activeToolId, setActiveToolId] = useState('pen');
  const [activeColorId, setActiveColorId] = useState('red');
  const [thickness, setThickness] = useState(2);
  const [fadeSeconds, setFadeSeconds] = useState(DEFAULT_FADE_SECONDS);

  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const webviewerInstanceRef = useRef(null);
  const annotManagerRef = useRef(null);
  const isApplyingRemoteChange = useRef(false);
  const currentPageRef = useRef(1);
  const totalPagesRef = useRef(1);
  const fadeTimersRef = useRef(new Map());
  const fadeSecondsRef = useRef(fadeSeconds);
  const scrollThrottleRef = useRef(0);

  useEffect(() => { fadeSecondsRef.current = fadeSeconds; }, [fadeSeconds]);

  // Keep refs in sync so keyboard handler always reads latest values
  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);
  useEffect(() => { totalPagesRef.current = totalPages; }, [totalPages]);

  // Sync with external state (e.g. from another host)
  useEffect(() => {
    if (presentationState?.currentSlide && presentationState.currentSlide !== currentPageRef.current) {
      console.log('[Presentation] Syncing to slide', presentationState.currentSlide, 'from', currentPageRef.current);
      setCurrentPage(presentationState.currentSlide);

      // Navigate the Apryse viewer to the synced page
      const instance = webviewerInstanceRef.current;
      if (instance) {
        const { documentViewer } = instance.Core;
        if (documentViewer.getDocument() && documentViewer.getCurrentPage() !== presentationState.currentSlide) {
          isApplyingRemoteChange.current = true;
          documentViewer.setCurrentPage(presentationState.currentSlide);
          setTimeout(() => { isApplyingRemoteChange.current = false; }, 300);
        }
      }
    }
  }, [presentationState?.currentSlide]);

  // ============================================================
  // Tool styling helpers (mirrors ApryseContractRenderer)
  // ============================================================
  const applyToolStyles = useCallback((toolId, colorId, thicknessOverride) => {
    const instance = webviewerInstanceRef.current;
    if (!instance) return;
    const { Core } = instance;
    const preset = getToolById(toolId);
    if (!preset) return;
    const color = getColorById(colorId);
    const tool = Core.documentViewer.getTool(preset.toolName);
    if (!tool) return;

    const styles = {
      StrokeColor: new Core.Annotations.Color(
        color.rgb.R, color.rgb.G, color.rgb.B, color.rgb.A
      ),
      StrokeThickness:
        thicknessOverride != null ? thicknessOverride : preset.defaultThickness,
      Opacity: preset.defaultOpacity,
    };
    if (!preset.filled) {
      styles.FillColor = new Core.Annotations.Color(0, 0, 0, 0);
    }
    tool.setStyles(styles);
  }, []);

  const scheduleFade = useCallback((annotation) => {
    const annotManager = annotManagerRef.current;
    if (!annotManager) return;
    if (fadeTimersRef.current.has(annotation.Id)) return;

    const timer = setTimeout(() => {
      const steps = 8;
      let i = 0;
      const startOpacity = annotation.Opacity ?? 1;
      const tick = setInterval(() => {
        i++;
        annotation.Opacity = startOpacity * (1 - i / steps);
        try { annotManager.redrawAnnotation(annotation); } catch (_) {}
        if (i >= steps) {
          clearInterval(tick);
          try {
            annotManager.deleteAnnotation(annotation, { force: true, imported: false });
          } catch (_) {}
          fadeTimersRef.current.delete(annotation.Id);
        }
      }, 50);
    }, fadeSecondsRef.current * 1000);

    fadeTimersRef.current.set(annotation.Id, timer);
  }, []);

  const emitPresentationEvent = useCallback((type, payload) => {
    if (!meetingState?.emitPresentationEvent) return;
    meetingState.emitPresentationEvent({ presentationId, type, payload });
  }, [meetingState, presentationId]);

  // Initialize Apryse WebViewer
  useEffect(() => {
    if (!fileUrl) return;

    let cancelled = false;

    const initViewer = async () => {
      try {
        setLoading(true);
        setError(null);

        if (cancelled || !viewerRef.current) return;

        // Always-disabled elements (both host and participant). Print and
        // built-in page navigation are off for everyone since we render our
        // own controls.
        const baseDisabled = [
          'searchPanel',
          'searchButton',
          'leftPanel',
          'leftPanelButton',
          'menuButton',
          'toggleNotesButton',
          'pageNavOverlay',
          'pageNavigation',
          'previousPageButton',
          'nextPageButton',
          'pageNumberInput',
          'pageNumberDisplay',
          'printButton',
          'printModal',
        ];

        // Apryse's default chrome (the View / Annotate / Shapes / Insert /
        // Edit / Fill and Sign / Forms ribbon + right-side panel toggles).
        // Hidden for everyone in the presentation view.
        const apryseChromeDisabled = [
          'documentHeader',
          'header',
          'toolsHeader',
          'toolbar',
          'ribbon',
          'ribbons',
          'toolbarGroup-View',
          'toolbarGroup-Annotate',
          'toolbarGroup-Shapes',
          'toolbarGroup-Insert',
          'toolbarGroup-Edit',
          'toolbarGroup-FillAndSign',
          'toolbarGroup-Forms',
          'viewControlsButton',
          'viewControlsOverlay',
          'zoomOverlay',
          'zoomOverlayButton',
          'zoomInButton',
          'zoomOutButton',
          'zoomFitButton',
          'panToolButton',
          'selectToolButton',
          'marqueeToolButton',
          'cropToolButton',
          'toolsButton',
          'documentControl',
          'documentControlContainer',
          'multiPageManipulationControlsButton',
          'multiPageManipulationControls',
          'firstPageButton',
          'lastPageButton',
          'default-top-header',
          'default-tools-header',
          'default-bottom-header',
          'default-left-header',
          'default-right-header',
          'tools-header',
          'top-header',
          'bottom-header',
          'default-ribbon-group',
          'page-controls-container',
          'annotationToolsHeader',
          'rightPanel',
          'rightPanelButton',
          'outlinesPanel',
          'outlinesPanelButton',
          'bookmarksPanel',
          'bookmarksPanelButton',
          'thumbnailsPanel',
          'thumbnailsPanelButton',
          'notesPanel',
          'signaturePanel',
          'signaturePanelButton',
          'attachmentPanel',
          'attachmentPanelButton',
        ];

        const disabledElementsList = [...baseDisabled, ...apryseChromeDisabled];

        const { instance } = await createApryseWebViewer(viewerRef.current, {
          fileUrl,
          fileType,
          filename: name,
          // Annotations fully disabled — the presentation is a read-only
          // viewer (no drawing tools, no annotation popup on click/select).
          enableAnnotations: false,
          disabledElements: disabledElementsList,
        });

        if (cancelled) return;

        const { UI, Core } = instance;
        const { documentViewer, annotationManager } = Core;
        annotManagerRef.current = annotationManager;
        annotationManager.setCurrentUser(isHost ? 'host' : 'participant');

        // Single-page layout for slide-like experience
        UI.setLayoutMode(UI.LayoutMode.Single);
        UI.disableElements(disabledElementsList);

        // Disable the built-in print feature so Ctrl/Cmd+P inside the
        // viewer doesn't open Apryse's print modal.
        try {
          UI.disableFeatures?.([UI.Feature.Print]);
          UI.hotkeys?.off?.(UI.hotkeys.Keys.CTRL_P);
          UI.hotkeys?.off?.(UI.hotkeys.Keys.COMMAND_P);
        } catch (_) {}

        // Wipe the header and inject CSS to hide any Apryse chrome that
        // slipped past the disabledElements list (different versions name
        // some elements differently). Applies to host and participant alike
        // in the presentation view.
        {
          try {
            if (UI.setHeaderItems) UI.setHeaderItems(() => []);
          } catch (_) {}

          const STYLE_ID = 'rlink-presentation-hide-chrome';
          const HIDE_CSS = `
                  /* Nuclear: hide every direct child of .App except the
                     document viewport itself, plus any sibling header/toolbar
                     no matter what Apryse calls it in this version. */
                  .App > *:not(.MainWindow):not([class*="Document"]) {
                    display: none !important;
                  }
                  .MainWindow > *:not([class*="Document"]):not([class*="document"]) {
                    display: none !important;
                  }
                  [class*="Header"], [class*="header"],
                  [class*="Toolbar"], [class*="toolbar"],
                  [class*="Ribbon"], [class*="ribbon"],
                  [class*="ModularHeader"], [class*="GroupedItems"],
                  [class*="ToolGroup"], [class*="ToolStyle"],
                  [class*="StylePopup"], [class*="DocumentControl"],
                  [data-element*="Header"], [data-element*="header"],
                  [data-element*="Toolbar"], [data-element*="toolbar"],
                  [data-element*="ribbon"], [data-element*="Ribbon"],
                  [data-element*="ToolButton"], [data-element*="ToolGroupButton"],
                  [data-element*="ToolStyleButton"], [data-element*="StylePopup"],
                  [data-element*="documentControl"],
                  [data-element*="pageNavigation"],
                  [data-element*="rightPanel"], [data-element*="leftPanel"],
                  [data-element*="zoomOverlay"], [data-element*="zoomIn"],
                  [data-element*="zoomOut"], [data-element*="searchButton"],
                  [data-element*="menuButton"],
                  [data-element^="toolbarGroup-"],
                  [data-element^="freeText"], [data-element^="freehand"],
                  [data-element^="highlight"], [data-element^="underline"],
                  [data-element^="strikeout"], [data-element^="squiggly"],
                  [data-element^="stickyTool"], [data-element^="signatureTool"],
                  [data-element^="undoButton"], [data-element^="redoButton"],
                  [data-element^="eraserToolButton"],
                  [data-element="textPopup"],
                  [data-element="annotationPopup"],
                  [data-element="contextMenuPopup"] {
                    display: none !important;
                    height: 0 !important;
                    visibility: hidden !important;
                  }
                  /* Make sure the document fills the cleared space. */
                  .App, .MainWindow,
                  [class*="DocumentContainer"], [class*="Document"][class*="Container"],
                  [data-element="documentContainer"] {
                    top: 0 !important;
                    height: 100% !important;
                  }
                  /* Force default cursor (no Pan-mode hand) and disable text
                     selection so Apryse's text-popup never appears. Use a
                     universal selector so we beat Apryse's inline cursor
                     styles regardless of which inner element it sets them on. */
                  html, body, * {
                    cursor: default !important;
                    user-select: none !important;
                    -webkit-user-select: none !important;
                  }
                `;

          const injectInto = (doc) => {
            try {
              if (!doc) return;
              if (doc.getElementById(STYLE_ID)) return;
              const style = doc.createElement('style');
              style.id = STYLE_ID;
              style.textContent = HIDE_CSS;
              (doc.head || doc.documentElement).appendChild(style);
            } catch (_) {}
          };

          // Block the browser/Apryse right-click context menu inside the
          // viewer so right-click shows nothing in the presentation.
          const blockContextMenu = (target) => {
            try {
              if (!target) return;
              target.addEventListener(
                'contextmenu',
                (e) => { e.preventDefault(); e.stopPropagation(); },
                true
              );
            } catch (_) {}
          };

          // 1) Parent doc — covers anything Apryse portals out of the iframe
          //    or renders inline (no iframe).
          injectInto(document);
          blockContextMenu(viewerRef.current);

          // 2) Iframe doc — Apryse's main UI lives here.
          const iframe = viewerRef.current?.querySelector('iframe');
          if (iframe) {
            const ensureCSS = () => {
              const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
              injectInto(iframeDoc);
              blockContextMenu(iframeDoc);
            };
            iframe.addEventListener('load', ensureCSS);
            setTimeout(ensureCSS, 500);
            setTimeout(ensureCSS, 1500);
            setTimeout(ensureCSS, 3000);

            const watchForRemount = () => {
              try {
                const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
                if (!iframeDoc?.body) return;
                const observer = new MutationObserver(() => ensureCSS());
                observer.observe(iframeDoc.body, { childList: true, subtree: true });
                webviewerInstanceRef.current.__chromeObserver = observer;
              } catch (_) {}
            };
            iframe.addEventListener('load', watchForRemount);
            setTimeout(watchForRemount, 1500);
          }
        }

        // Disable page navigation for non-hosts
        if (!isHost) {
          const handleKeyDown = (e) => {
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End'].includes(e.key)) {
              e.preventDefault();
              e.stopPropagation();
            }
          };
          document.addEventListener('keydown', handleKeyDown);
          // We'll clean up in the main cleanup
        }

        // Make instance available before documentLoaded so tool helpers work.
        webviewerInstanceRef.current = instance;

        // For Office docs (DOC / DOCX / PPTX) Apryse paginates asynchronously
        // after `documentLoaded` fires, so the initial getPageCount() can
        // return 1 even when the doc has many pages. Re-read on the events
        // Apryse emits when layout is complete.
        const refreshPageCount = () => {
          if (cancelled) return;
          const count = documentViewer.getPageCount();
          if (count > 0) setTotalPages(count);
        };
        documentViewer.addEventListener('pagesUpdated', refreshPageCount);
        documentViewer.addEventListener('pageCountUpdated', refreshPageCount);

        // Listen for document loaded
        documentViewer.addEventListener('documentLoaded', () => {
          if (cancelled) return;
          const pageCount = documentViewer.getPageCount();
          setTotalPages(pageCount);
          setCurrentPage(1);
          setLoading(false);

          // If we need to sync to a specific slide on load
          if (presentationState?.currentSlide && presentationState.currentSlide > 1) {
            isApplyingRemoteChange.current = true;
            documentViewer.setCurrentPage(presentationState.currentSlide);
            setCurrentPage(presentationState.currentSlide);
            setTimeout(() => { isApplyingRemoteChange.current = false; }, 300);
          }

          if (isHost) {
            UI.setToolMode('Pan');

            // Scroll sync — host's scroll position broadcasts to participants
            const scrollEl = documentViewer.getScrollViewElement();
            if (scrollEl) {
              const onScroll = () => {
                if (isApplyingRemoteChange.current) return;
                const now = Date.now();
                if (now - scrollThrottleRef.current < 50) return;
                scrollThrottleRef.current = now;
                emitPresentationEvent('scroll', {
                  top: scrollEl.scrollTop,
                  left: scrollEl.scrollLeft,
                  height: scrollEl.scrollHeight,
                  width: scrollEl.scrollWidth,
                });
              };
              scrollEl.addEventListener('scroll', onScroll, { passive: true });
            }
          }
        });

        if (isHost) {
          documentViewer.addEventListener('zoomUpdated', (zoom) => {
            if (isApplyingRemoteChange.current) return;
            emitPresentationEvent('zoom', { zoom });
          });
        }

        // Listen for page changes from the viewer (e.g. scrolling, internal nav)
        documentViewer.addEventListener('pageNumberUpdated', (pageNumber) => {
          if (isApplyingRemoteChange.current) return;

          // Non-hosts: revert any manual page change
          if (!isHost) {
            const expected = currentPageRef.current;
            if (pageNumber !== expected) {
              isApplyingRemoteChange.current = true;
              documentViewer.setCurrentPage(expected);
              setTimeout(() => { isApplyingRemoteChange.current = false; }, 100);
            }
            return;
          }

          // Host: update state and broadcast
          setCurrentPage(pageNumber);
          onPageChange?.(pageNumber);
          onStateUpdate?.({ ...presentationState, currentSlide: pageNumber, status: 'active' });
          emitPresentationEvent('page', { page: pageNumber });
        });

        // Annotation handling — only host broadcasts; everyone schedules fade.
        annotationManager.addEventListener(
          'annotationChanged',
          async (annotations, action, info) => {
            if (info?.imported) return;
            if (isApplyingRemoteChange.current) return;
            if (action === 'add') {
              annotations.forEach((a) => scheduleFade(a));
            }
            if (!isHost) return;
            try {
              const xfdfCommand = await annotationManager.exportAnnotationCommand();
              if (!xfdfCommand) return;
              emitPresentationEvent('annotation', { xfdfCommand, action });
            } catch (err) {
              console.error('[Presentation] export annotation failed', err);
            }
          }
        );

        // Inbound sync — both roles listen
        const unsubscribe = meetingState?.onPresentationEvent
          ? meetingState.onPresentationEvent(presentationId, async ({ type, payload }) => {
              const inst = webviewerInstanceRef.current;
              if (!inst) return;
              isApplyingRemoteChange.current = true;
              try {
                if (type === 'scroll') {
                  const scrollEl = inst.Core.documentViewer.getScrollViewElement();
                  if (scrollEl && payload) {
                    const heightRatio = payload.height
                      ? scrollEl.scrollHeight / payload.height
                      : 1;
                    const widthRatio = payload.width
                      ? scrollEl.scrollWidth / payload.width
                      : 1;
                    scrollEl.scrollTop = payload.top * heightRatio;
                    scrollEl.scrollLeft = payload.left * widthRatio;
                  }
                } else if (type === 'zoom') {
                  inst.Core.documentViewer.zoomTo(payload.zoom);
                } else if (type === 'page') {
                  inst.Core.documentViewer.setCurrentPage(payload.page);
                  setCurrentPage(payload.page);
                } else if (type === 'annotation' && payload?.xfdfCommand) {
                  const imported = await annotationManager.importAnnotationCommand(
                    payload.xfdfCommand
                  );
                  if (Array.isArray(imported)) {
                    annotationManager.drawAnnotationsFromList(imported);
                    if (payload.action === 'add') {
                      imported.forEach((a) => scheduleFade(a));
                    }
                  }
                } else if (type === 'clear') {
                  const existing = annotationManager.getAnnotationsList();
                  if (existing.length) {
                    annotationManager.deleteAnnotations(existing, {
                      force: true,
                      imported: true,
                    });
                  }
                }
              } finally {
                setTimeout(() => { isApplyingRemoteChange.current = false; }, 50);
              }
            })
          : null;

        // Stash unsubscribe on the ref so cleanup can call it
        webviewerInstanceRef.current.__unsubscribe = unsubscribe;
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to initialize Apryse WebViewer:', err);
        setError('Failed to load presentation. Please try again.');
        setLoading(false);
      }
    };

    initViewer();

    return () => {
      cancelled = true;
      // Clear any pending fade timers
      fadeTimersRef.current.forEach((t) => clearTimeout(t));
      fadeTimersRef.current.clear();
      if (webviewerInstanceRef.current) {
        try { webviewerInstanceRef.current.__unsubscribe?.(); } catch (_) {}
        try { webviewerInstanceRef.current.__chromeObserver?.disconnect(); } catch (_) {}
        try {
          webviewerInstanceRef.current.UI.dispose();
        } catch (_) {}
        webviewerInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUrl, fileType, isHost, presentationId]);

  // ============================================================
  // Annotation toolbar handlers (host only)
  // ============================================================
  const handleSelectTool = useCallback((toolId) => {
    const preset = getToolById(toolId);
    if (!preset) return;
    setActiveToolId(toolId);
    applyToolStyles(toolId, activeColorId, preset.defaultThickness);
    setThickness(preset.defaultThickness);
    webviewerInstanceRef.current?.UI.setToolMode(preset.toolName);
  }, [activeColorId, applyToolStyles]);

  const handleSelectColor = useCallback((colorId) => {
    setActiveColorId(colorId);
    applyToolStyles(activeToolId, colorId, thickness);
  }, [activeToolId, thickness, applyToolStyles]);

  const handleThicknessChange = useCallback((value) => {
    setThickness(value);
    applyToolStyles(activeToolId, activeColorId, value);
  }, [activeToolId, activeColorId, applyToolStyles]);

  const handleClearAll = useCallback(() => {
    const annotManager = annotManagerRef.current;
    if (!annotManager) return;
    const list = annotManager.getAnnotationsList();
    if (!list.length) return;
    annotManager.deleteAnnotations(list, { force: true });
    fadeTimersRef.current.forEach((t) => clearTimeout(t));
    fadeTimersRef.current.clear();
    emitPresentationEvent('clear', {});
  }, [emitPresentationEvent]);

  // Page navigation helpers
  const goToPage = useCallback((pageNum) => {
    const clamped = Math.max(1, Math.min(pageNum, totalPagesRef.current));
    if (clamped === currentPageRef.current) return;

    setCurrentPage(clamped);

    const instance = webviewerInstanceRef.current;
    if (instance) {
      isApplyingRemoteChange.current = true;
      instance.Core.documentViewer.setCurrentPage(clamped);
      setTimeout(() => { isApplyingRemoteChange.current = false; }, 300);
    }

    onPageChange?.(clamped);
    onStateUpdate?.({ ...presentationState, currentSlide: clamped, status: 'active' });
  }, [onPageChange, onStateUpdate, presentationState]);

  const handleNext = useCallback(() => {
    goToPage(currentPageRef.current + 1);
  }, [goToPage]);

  const handlePrevious = useCallback(() => {
    goToPage(currentPageRef.current - 1);
  }, [goToPage]);

  // Keyboard navigation (host only)
  useEffect(() => {
    if (!isHost) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        handlePrevious();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onPresentationEnd?.();
      } else if (e.key === 'Home') {
        e.preventDefault();
        goToPage(1);
      } else if (e.key === 'End') {
        e.preventDefault();
        goToPage(totalPagesRef.current);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isHost, handleNext, handlePrevious, goToPage, onPresentationEnd]);

  // Suppress Ctrl/Cmd+P while a presentation is open so the print modal
  // (browser or Apryse) never appears for any role.
  useEffect(() => {
    const blockPrint = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', blockPrint, true);
    return () => window.removeEventListener('keydown', blockPrint, true);
  }, []);

  // Fullscreen tracking
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleZoomIn = () => {
    setScale(prev => {
      const next = Math.min(2, prev + 0.25);
      const instance = webviewerInstanceRef.current;
      if (instance) {
        instance.Core.documentViewer.zoomTo(next);
      }
      return next;
    });
  };

  const handleZoomOut = () => {
    setScale(prev => {
      const next = Math.max(0.5, prev - 0.25);
      const instance = webviewerInstanceRef.current;
      if (instance) {
        instance.Core.documentViewer.zoomTo(next);
      }
      return next;
    });
  };

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  const toggleFullscreen = async () => {
    if (isIOS) {
      setMobileExpanded(prev => !prev);
      return;
    }
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      await containerRef.current.requestFullscreen();
    } else {
      await document.exitFullscreen();
    }
  };

  // No file URL — show error
  if (!fileUrl) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 z-30 flex items-center justify-center bg-black/95"
      >
        <div className="text-center p-8 max-w-md">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">No File Found</h3>
          <p className="text-gray-400 mb-6">
            This presentation doesn't have a file. Please edit and upload one.
          </p>
          <button
            onClick={onPresentationEnd}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
          >
            Close
          </button>
        </div>
      </motion.div>
    );
  }

  const isExpandedFullscreen = isFullscreen || mobileExpanded;

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`fixed flex flex-col bg-black transition-all duration-300 ${
        (isExpandedFullscreen || isRecorderMode) ? 'left-0 right-0' : (!leftSidebarCollapsed ? 'left-64 md:left-[380px]' : 'left-14')
      } ${
        (isExpandedFullscreen || isRecorderMode) ? '' : (!rightSidebarCollapsed ? 'right-72 md:right-80' : 'right-14')
      }`}
      style={{
        top: (isExpandedFullscreen || isRecorderMode) ? 0 : 64,
        bottom: (isExpandedFullscreen || isRecorderMode) ? 0 : 64,
        zIndex: isExpandedFullscreen ? 99999 : 50,
      }}
    >
      {/* Loading Overlay */}
      <AnimatePresence>
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-40 flex items-center justify-center bg-black/80"
          >
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-[#6a1fbf] mx-auto mb-4 animate-spin" />
              <p className="text-white text-lg">Loading presentation...</p>
              <p className="text-gray-400 text-sm mt-1">{name}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Overlay */}
      {error && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/90">
          <div className="text-center p-8 max-w-md">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Failed to Load</h3>
            <p className="text-gray-400 mb-6">{error}</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => {
                  setError(null);
                  setLoading(true);
                  // Force re-mount by clearing and re-setting
                  if (webviewerInstanceRef.current) {
                    try { webviewerInstanceRef.current.UI.dispose(); } catch (_) {}
                    webviewerInstanceRef.current = null;
                  }
                }}
                className="px-4 py-2 bg-[#6a1fbf] hover:bg-[#5a1aaf] rounded-lg text-white transition-colors"
              >
                Retry
              </button>
              <button
                onClick={onPresentationEnd}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content Area — Apryse renders every supported type (PDF, DOC/DOCX,
          PPT/PPTX-converted-PDF, image). */}
      <div className="flex-1 relative overflow-hidden">
        <div
          ref={viewerRef}
          className="w-full h-full"
          style={{
            pointerEvents: !isHost ? 'none' : 'auto',
            touchAction: !isHost ? 'none' : 'auto',
          }}
        />
      </div>

      {/* Host Controls */}
      {isHost && (
        <div className="absolute bottom-3 sm:bottom-6 left-1/2 -translate-x-1/2 z-40 w-[95vw] sm:w-auto max-w-2xl">
          <div className="flex items-center gap-1 sm:gap-2 bg-[#001233]/95 backdrop-blur-xl border border-white/10 rounded-xl px-2 sm:px-4 py-1.5 sm:py-2 shadow-2xl">
            {/* Presentation Info - hidden on mobile */}
            <div className="hidden sm:flex items-center gap-2 text-white text-sm pr-3 border-r border-white/20">
              {fileType === 'pdf' ? (
                <FileText className="w-4 h-4 text-red-400" />
              ) : (
                <Image className="w-4 h-4 text-blue-400" />
              )}
              <span className="max-w-[150px] truncate">{name}</span>
            </div>

            {/* Navigation Controls */}
            <div className="flex items-center gap-0.5 sm:gap-1">
              <button
                onClick={handlePrevious}
                disabled={currentPage <= 1}
                className="p-1.5 sm:p-2 rounded-lg hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Previous (\u2190)"
              >
                <ChevronLeft className="w-4 sm:w-5 h-4 sm:h-5 text-white" />
              </button>

              <div className="px-1.5 sm:px-3 text-white text-xs sm:text-sm font-medium min-w-[50px] sm:min-w-[80px] text-center">
                {currentPage} / {totalPages}
              </div>

              <button
                onClick={handleNext}
                disabled={currentPage >= totalPages}
                className="p-1.5 sm:p-2 rounded-lg hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Next (\u2192)"
              >
                <ChevronRight className="w-4 sm:w-5 h-4 sm:h-5 text-white" />
              </button>
            </div>

            <div className="w-px h-5 sm:h-6 bg-white/20 mx-0.5 sm:mx-1" />

            {/* Zoom Controls - simplified on mobile */}
            <div className="flex items-center gap-0.5 sm:gap-1">
              <button
                onClick={handleZoomOut}
                disabled={scale <= 0.5}
                className="p-1.5 sm:p-2 rounded-lg hover:bg-white/10 disabled:opacity-30 transition-colors"
                title="Zoom out"
              >
                <ZoomOut className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-white" />
              </button>
              <span className="text-white/60 text-[10px] sm:text-xs min-w-[30px] sm:min-w-[40px] text-center hidden sm:block">
                {Math.round(scale * 100)}%
              </span>
              <button
                onClick={handleZoomIn}
                disabled={scale >= 2}
                className="p-1.5 sm:p-2 rounded-lg hover:bg-white/10 disabled:opacity-30 transition-colors"
                title="Zoom in"
              >
                <ZoomIn className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-white" />
              </button>
            </div>

            <div className="w-px h-5 sm:h-6 bg-white/20 mx-0.5 sm:mx-1" />

            {/* Fullscreen Toggle */}
            <button
              onClick={toggleFullscreen}
              className="p-1.5 sm:p-2 rounded-lg hover:bg-white/10 transition-colors"
              title={isExpandedFullscreen ? "Exit fullscreen" : "Fullscreen"}
            >
              {isExpandedFullscreen ? (
                <Minimize2 className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-white" />
              ) : (
                <Maximize2 className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-white" />
              )}
            </button>

            <div className="w-px h-5 sm:h-6 bg-white/20 mx-0.5 sm:mx-1" />

            {/* End Button */}
            <button
              onClick={onPresentationEnd}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 transition-colors"
              title="End presentation (Esc)"
            >
              <X className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
              <span className="text-xs sm:text-sm">End</span>
            </button>
          </div>
        </div>
      )}

      {/* Participant View - Fullscreen toggle on mobile only */}
      {!isHost && isMobile && (
        <div className="absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 z-40">
          <div className="flex items-center gap-2 sm:gap-3 bg-black/60 backdrop-blur-sm rounded-lg px-3 sm:px-4 py-1.5 sm:py-2">
            <button
              onClick={toggleFullscreen}
              className="p-1.5 rounded hover:bg-white/10 transition-colors"
              title={isExpandedFullscreen ? 'Exit fullscreen' : 'Full screen'}
            >
              {isExpandedFullscreen ? (
                <Minimize2 className="w-5 h-5 text-white/80" />
              ) : (
                <Maximize2 className="w-5 h-5 text-white/80" />
              )}
            </button>
          </div>
        </div>
      )}

      {/* Keyboard Shortcuts Hint (Host Only, shows briefly) */}
      {isHost && (
        <motion.div
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ delay: 3, duration: 1 }}
          className="absolute top-4 right-4 z-40 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-2"
        >
          <p className="text-white/60 text-xs">
            &larr; &rarr; Navigate &bull; Esc Exit &bull; Home/End Jump
          </p>
        </motion.div>
      )}
    </motion.div>
  );
}
